import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAnnouncementsStore } from '../stores/announcements-store'
import type { AdminAnnouncement } from '../types/admin-announcement'
import {
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useDeleteAnnouncementMutation,
  getGetAdminAnnouncementsQueryKey,
  type AdminCreateAnnouncementRequest,
  type AdminUpdateAnnouncementRequest,
} from './use-admin-announce'

/**
 * 管理员公告业务意图层 Hook
 * 所有业务流程（CRUD操作、弹窗控制）都集中在此
 */
export function useAdminAnnouncementsActions() {
  const queryClient = useQueryClient()

  // 只拿 store 的原子 set
  const setDialogType = useAnnouncementsStore((s) => s.admin.setDialogType)
  const setCurrentRow = useAnnouncementsStore((s) => s.admin.setCurrentRow)

  // Mutations
  const createMutation = useCreateAnnouncementMutation()
  const updateMutation = useUpdateAnnouncementMutation()
  const deleteMutation = useDeleteAnnouncementMutation()

  const invalidateList = () => {
    queryClient.invalidateQueries({
      queryKey: getGetAdminAnnouncementsQueryKey(),
    })
  }

  return {
    // ===== CRUD 操作 =====
    /**
     * 创建公告
     */
    createAnnouncement: async (data: AdminCreateAnnouncementRequest) => {
      try {
        await createMutation.mutateAsync({ data })
        toast.success('公告创建成功')
        invalidateList()
        setDialogType(null)
      } catch {
        toast.error('创建公告失败')
      }
    },

    /**
     * 更新公告
     */
    updateAnnouncement: async (
      id: string,
      data: AdminUpdateAnnouncementRequest
    ) => {
      try {
        await updateMutation.mutateAsync({ id, data })
        toast.success('公告更新成功')
        invalidateList()
        setDialogType(null)
        setCurrentRow(null)
      } catch {
        toast.error('更新公告失败')
      }
    },

    /**
     * 删除公告
     */
    deleteAnnouncement: async (id: string) => {
      try {
        await deleteMutation.mutateAsync({ id })
        toast.success('公告删除成功')
        invalidateList()
        setDialogType(null)
        setCurrentRow(null)
      } catch {
        toast.error('删除公告失败')
      }
    },

    // ===== 弹窗控制 =====
    /**
     * 打开创建弹窗
     */
    openCreate: () => {
      setCurrentRow(null)
      setDialogType('create')
    },

    /**
     * 打开编辑弹窗
     */
    openEdit: (announcement: AdminAnnouncement) => {
      setCurrentRow(announcement)
      setDialogType('update')
    },

    /**
     * 打开删除确认弹窗
     */
    openDelete: (announcement: AdminAnnouncement) => {
      setCurrentRow(announcement)
      setDialogType('delete')
    },

    /**
     * 关闭弹窗
     */
    closeDialog: () => {
      setDialogType(null)
      setTimeout(() => {
        setCurrentRow(null)
      }, 300)
    },

    // ===== Mutation 状态 =====
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
